import { useMemo, useState } from 'react';
import { Avatar, AvatarFallback, AvatarImage, Button } from '@monksflow/monks-ui';
import { TagInput } from '@/shared/ui';
import { getInitials } from '@/shared/utils/getInitials';
import type { WorkspaceUser } from '@/entities/project-member';
import type { AddMembersSectionProps } from './AddMembersSection.types';
import { useTranslation } from 'react-i18next';

/**
 * Uses the v4-based `<TagInput>` compound instead of the MUI-transitive
 * `TagInputField`. Zero MUI imports.
 *
 * The combobox in this section keeps its accessible name "Add members"
 * via the explicit `aria-label` attribute on `<TagInput.Chips>`'s input
 * (forwarded by the chips composition).
 */
export function AddMembersSection(props: AddMembersSectionProps) {
  const { t } = useTranslation('projects');

  const label = props.label ?? t('manageProjectMembers.addMembersSection.addMembers');
  const [selectedUsers, setSelectedUsers] = useState<WorkspaceUser[]>([]);

  // Exclude users who are already members of the project — but DO include
  // users that the host has staged for invitation (`selectedUsers`). They
  // need to remain in the items array so base-ui Combobox can render them
  // with `aria-selected=true` (the standard multi-select pattern). Removing
  // them from `items` while they're in `value` confuses base-ui's selection
  // commit — a click on a not-yet-selected item never registers because the
  // resulting state mutation re-renders the items list and base-ui loses
  // the row index between pointerdown and click.
  const availableUsers = useMemo(
    () => props.workspaceUsers.filter((user) => !props.existingMemberIds.includes(user.id)),
    [props.workspaceUsers, props.existingMemberIds],
  );

  const handleAdd = () => {
    if (selectedUsers.length === 0) return;

    props.onAdd(selectedUsers);
    setSelectedUsers([]);
  };

  return (
    <div className="flex items-start gap-3">
      <div className="flex-1">
        <TagInput<WorkspaceUser>
          value={selectedUsers}
          onChange={setSelectedUsers}
          options={availableUsers}
          getOptionLabel={(user) => user.name}
          getOptionValue={(user) => user.id}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          inputValue={props.searchInput}
          onInputValueChange={props.onSearchChange}
          disabled={props.isLoadingUsers}
        >
          <TagInput.Chips<WorkspaceUser>
            placeholder={label}
            ariaLabel={label}
            // Pin the field to 44px (min-h-11) so it doesn't grow when the
            // first member is selected (default is min-h-9). Also force the
            // horizontal padding to stay px-2.5 once chips appear — the DS
            // otherwise drops it to px-1.5, which shifts the ml-auto chevron
            // a few pixels to the right.
            className="min-h-11 has-data-[slot=combobox-chip]:px-2.5"
            renderChip={(user, onRemove) => (
              <TagInput.Chip
                key={user.id}
                title={user.email}
                avatar={
                  <Avatar size="xs">
                    {user.avatarUrl && <AvatarImage src={user.avatarUrl} alt={user.name} />}
                    <AvatarFallback size="xs">{getInitials(user.name)}</AvatarFallback>
                  </Avatar>
                }
                label={user.name}
                removeLabel={t('manageProjectMembers.addMembersSection.removeUser', {
                  name: user.name,
                })}
                onRemove={onRemove}
              />
            )}
          />
          <TagInput.Content<WorkspaceUser>
            renderOption={(user) => (
              <div className="flex items-center gap-3">
                <Avatar className="size-8">
                  {user.avatarUrl && <AvatarImage src={user.avatarUrl} alt={user.name} />}
                  <AvatarFallback>{getInitials(user.name)}</AvatarFallback>
                </Avatar>
                <div className="flex flex-col">
                  <span className="text-sm text-text-primary">{user.name}</span>
                  {user.email && <span className="text-xs text-text-secondary">{user.email}</span>}
                </div>
              </div>
            )}
          />
        </TagInput>
      </div>

      <Button
        variant="secondary"
        size="sm"
        onClick={handleAdd}
        disabled={selectedUsers.length === 0 || props.isAdding}
        // Match the 44px field height (h-11) with 16px/10px x/y padding,
        // overriding the sm size's defaults.
        className="h-11 px-4 py-2.5"
      >
        {t('manageProjectMembers.addMembersSection.add')}
      </Button>
    </div>
  );
}
